import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box as NivoBox } from '@nivo/core';
import { ResponsivePie } from '@nivo/pie';
import ResizeObserver from 'resize-observer-polyfill';

import { useTimeout } from '../../../common/timeout.hook';
import { DashboardChartData } from '../Dashboard.interface';
import styled from 'styled-components';

const StyledDiv = styled.div`
  width: 100%;
  height: 100%;
`;

export interface DashboardChartProps {
  data: DashboardChartData | undefined;
}

const DashboardChart: React.FC<DashboardChartProps> = (
  props: DashboardChartProps
) => {
  const { data } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [widthDiff, setWidthDiff] = useState<number>(0);
  const [observer, setObserver] = useState<ResizeObserver | undefined>();
  const [debounceUpdate] = useTimeout();
  const isEmpty = !data || data.total === 0;

  const margin: NivoBox = useMemo(() => {
    return { top: 30, bottom: 30, right: 30 + widthDiff, left: 30 + widthDiff };
  }, [widthDiff]);

  const pieData = useMemo(() => {
    const { assigned = 0, dueSoon = 0, overdue = 0 } = data || {};
    return [
      { id: 'Assigned', label: `assigned (${assigned})`, value: assigned },
      { id: 'Due Soon', label: `due soon (${dueSoon})`, value: dueSoon },
      { id: 'Overdue', label: `overdue (${overdue})`, value: overdue }
    ];
  }, [data]);

  const updateSize = () => {
    const { current: el } = containerRef;
    // get the <g> element that contains the chart
    const g: SVGGElement | null = el?.querySelector('svg > g') || null;
    if (!el || !g) {
      return;
    }
    const elRect: DOMRect = el.getBoundingClientRect();
    const gRect: DOMRect = g.getBoundingClientRect();
    const diff: number = elRect.width - gRect.width;
    const adiff: number = Math.ceil(Math.abs(diff));
    const widthDiff: number = adiff < 3 ? 10 : diff < 0 ? adiff : 0;
    setWidthDiff(widthDiff);
  };

  useLayoutEffect(() => {
    observer?.disconnect();
    const ob: ResizeObserver = new ResizeObserver(() => {
      debounceUpdate(() => updateSize(), 100);
    });
    setObserver(ob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const { current: el } = containerRef;
    if (el && observer) {
      observer.observe(el);
    }
  }, [observer]);

  return !data ? null : (
    <StyledDiv
      ref={containerRef}
      className={isEmpty ? 'img-empty-container' : undefined}
    >
      {isEmpty ? (
        <img
          className='img-empty'
          alt='Empty Pending Document Reviews'
          src='/assets/pending-document-empty-state.png'
        />
      ) : (
        <ResponsivePie
          data={pieData}
          margin={margin}
          innerRadius={0.5}
          activeOuterRadiusOffset={8}
          padAngle={0.7}
          cornerRadius={3}
          colors={['#61cdbb', 'rgb(241, 225, 91)', '#f47560']}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0]] }}
          arcLabel='label'
          arcLabelsSkipAngle={10}
          arcLinkLabelsTextOffset={6}
          arcLabelsTextColor='#333333'
          arcLinkLabelsOffset={0}
          arcLinkLabelsDiagonalLength={16}
          arcLinkLabelsStraightLength={24}
          arcLinkLabelsThickness={1}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor='#333333'
          animate={true}
        />
      )}
    </StyledDiv>
  );
};

export default DashboardChart;
